import React from "react";
import { registerView } from "@frui.ts/views";
import type { ViewComponent } from "@frui.ts/views";
import AccountingDocumentsApprovalsViewModel from "../../../viewModels/finance/accountingDocumentsApprovals/accountingDocumentsApprovalsViewModel";
import { observer } from "mobx-react-lite";
import type { ColumnDefinition } from "@frui.ts/dataviews/dist/dataTypes";
import type AccountingDocumentCandidateResponse from "../../../entities/accountingDocumentCandidateResponse";
import { formatAmount, formatDate } from "../../../utils/localizations";
import ListView from "../../common/list";
import { ApprovalColumns } from "../../common/approvalColumns";
import AccountingDocumentsApprovalsFilters from "./accountingDocumentsApprovalsFilters";
import Button from "@edaz/edazkit/Button/index";
import { Check, CollectionCheck } from "@frui.ts/bootstrap";
import Icon, { IconType } from "@edaz/edazkit/Icon";
import Tooltip from "@edaz/edazkit/Tooltip/index";

const AccountingDocumentsApprovalsView: ViewComponent<AccountingDocumentsApprovalsViewModel> = observer(({ vm }) => {
  const columns: ColumnDefinition<AccountingDocumentCandidateResponse>[] = [
    {
      title: <Check custom target={vm as { allItems?: boolean }} property="allItems" threeState={true} />,
      valueFormatter: props => {
        if (vm.myApprovalStep(props.item) && !vm.approvedByMe(props.item)) {
          return <CollectionCheck custom target={vm} property="selectedItems" disabled={false} value={props.item} />;
        }

        let icon;
        let tooltip;
        if (vm.approvedByMe(props.item)) {
          icon = IconType.Check;
          tooltip = <span>{vm.translate("alreadyApproved")}</span>;
        } else {
          icon = IconType.Info;
          tooltip = <span>{vm.translate("notMyApprovalStep")}</span>;
        }

        return (
          <Tooltip id={`help-${props.item.id}`} tooltip={tooltip}>
            <span style={{ cursor: "help" }}>
              <Icon icon={icon} width={20} height={20} />
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: vm.translateAttribute("accountingDate"),
      property: "accountingDate",
      headerFormatter: props => {
        return (
          <th key={props.key} style={{ minWidth: 135, whiteSpace: "normal", textAlign: "right" }}>
            {props.column.titleFactory ? props.column.titleFactory(props.context) : props.column.title}
          </th>
        );
      },
      valueFormatter: props => formatDate(props.value),
    },
    {
      title: vm.translateAttribute("accountingPeriod"),
      property: "accountingPeriod",
    },
    {
      title: vm.translateAttribute("creditSum"),
      property: "creditSum",
      valueFormatter: props => formatAmount(props.value),
    },
    {
      title: vm.translateAttribute("debitSum"),
      property: "debitSum",
      valueFormatter: props => formatAmount(props.value),
    },
    {
      title: vm.translateAttribute("sequenceName"),
      property: "sequenceName",
    },
    {
      title: vm.translateAttribute("accountingTemplateId"),
      property: "accountingTemplateId",
      valueFormatter: props => vm.translateAccountingTemplateCode(props.value),
    },
    {
      title: vm.translateAttribute("documentHeaderDescription"),
      property: "documentHeaderDescription",
    },
    {
      title: vm.translateAttribute("chargeId"),
      property: "chargeId",
    },
    ...ApprovalColumns<AccountingDocumentCandidateResponse, AccountingDocumentsApprovalsViewModel>(vm),
  ];

  const exportApprovedButton = (
    <Button
      size="sm"
      key="export-approved-accounting-documents"
      variant="primary"
      className="rounded"
      onClick={vm.onExportApprovedClick}
      disabled={vm.noApprovalsForExport}>
      {vm.translateModuleGeneral("exportApproved")}
    </Button>
  );

  const approveAllButton = (
    <Button className="rounded" size="sm" onClick={vm.onApproveAllClick} disabled={!vm.canApproveAll}>
      {vm.translate("approveAll")}
    </Button>
  );

  const approveSelectedButton = (
    <Button className="rounded" size="sm" onClick={vm.onApproveSelectedClick} disabled={!vm.canApproveSelected}>
      {vm.translate("approveSelected")}
    </Button>
  );

  return (
    <ListView
      showDetailWithList={true}
      columns={columns}
      vm={vm}
      aboveTable={<AccountingDocumentsApprovalsFilters vm={vm} />}
      headerButtons={[approveSelectedButton, approveAllButton, exportApprovedButton]}
    />
  );
});

export default AccountingDocumentsApprovalsView;
registerView(AccountingDocumentsApprovalsView, AccountingDocumentsApprovalsViewModel);
